import { Localized } from '../../internal/Localized';
import Styles from './Landing.module.css';
import { LandingConstants } from './LandingConstants';
import { Button, Label } from '@app/engine';
import { FormatLocalized, GetCurrencySymbol } from '@shared/common';
import { useEffect, useMemo, useState } from 'react';
import type { BillingCurrency, INicheContentDTO } from '@shared/common';

/** Props for the RoiCard. */
interface IRoiCardProps {
  niches: INicheContentDTO[];
  echoMonthlyCost: number;
  // Name of the plan the echo cost is drawn from (the cheapest plan), shown in the echo-cost row label.
  echoPlanName: string;
  // Currency every monetary value in the calculator is displayed in (matches the pricing cards' resolved currency).
  currency: BillingCurrency;
  onRecover: () => void;
}

/** A selectable trade option for the ROI calculator. */
interface ITradeOption {
  key: string;
  label: string;
  avgJobValue: number;
}

/** Props for a single range slider row. */
interface IRangeSliderProps {
  label: string;
  unit: string;
  min: number;
  max: number;
  value: number;
  minLabel: string;
  midLabel: string;
  maxLabel: string;
  onChange: (value: number) => void;
}

/** Props for a single ROI breakdown row. */
interface IRoiRowProps {
  label: string;
  value: string;
  tone?: 'neg' | 'pos';
}

/**
 * Formats a whole amount with thousands separators, prefixed with the supplied currency symbol.
 *
 * @param amount - The amount to format.
 * @param currencySymbol - The currency symbol to prefix (e.g. "$", "CA$").
 * @returns The formatted currency string (e.g. "CA$8,500").
 */
function FormatCurrency(amount: number, currencySymbol: string) {
  return `${currencySymbol}${Math.round(amount).toLocaleString('en-US')}`;
}

/**
 * A labelled range slider with a large live value readout.
 *
 * @param props - Component props.
 * @returns The range slider element.
 */
function RangeSlider(props: IRangeSliderProps) {
  return (
    <div className={Styles.inputGroup}>
      <Label text={props.label} classNameContainer={Styles.inputLabel} />
      <div className={Styles.rangeBox}>
        <div className={Styles.rangeValueDisplay}>
          <Label text={String(props.value)} classNameContainer={Styles.rangeBig} />
          <Label text={props.unit} classNameContainer={Styles.rangeUnit} />
        </div>
        {/* Raw <input>: range slider needing min/max and an event-arg onChange; engine Textbox is a text input forwarding neither. */}
        <input className={Styles.range} type="range" min={props.min} max={props.max} value={props.value} onChange={(event) => props.onChange(Number(event.target.value))} />
        <div className={Styles.rangeLabels}>
          <Label text={props.minLabel} classNameContainer={Styles.rangePill} />
          <Label text={props.midLabel} classNameContainer={Styles.rangePill} />
          <Label text={props.maxLabel} classNameContainer={Styles.rangePill} />
        </div>
      </div>
    </div>
  );
}

/**
 * A single line in the ROI breakdown (label + value).
 *
 * @param props - Component props.
 * @returns The breakdown row element.
 */
function RoiRow(props: IRoiRowProps) {
  const toneClass = props.tone === 'neg' ? Styles.neg : props.tone === 'pos' ? Styles.pos : undefined;
  return (
    <div className={Styles.roiRow}>
      <Label text={props.label} classNameContainer={Styles.roiRowLabel} />
      <Label text={props.value} classNameContainer={`${Styles.roiRowValue} ${toneClass ?? ''}`.trim()} />
    </div>
  );
}

/**
 * Card 4 — ROI calculator. Live estimate of revenue lost to missed calls.
 *
 * @param props - Component props.
 * @returns The ROI card element.
 */
export function RoiCard(props: IRoiCardProps) {
  // ROI trade options come from niches flagged for the ROI calculator that carry an average job value.
  const trades = useMemo<ITradeOption[]>(
    () =>
      props.niches
        .filter((entry) => entry.showInRoi && typeof entry.avgJobValue === 'number')
        .map((entry) => ({ key: entry.niche, label: entry.signupLabel, avgJobValue: entry.avgJobValue as number })),
    [props.niches]
  );

  const [tradeKey, setTradeKey] = useState<string>('');
  const [calls, setCalls] = useState<number>(LandingConstants.roiCallsDefault);
  const [closeRate, setCloseRate] = useState<number>(LandingConstants.roiCloseDefault);
  // Editable average job value (major-unit string). Defaults to the selected trade's niche value; an invalid entry falls back to that default for the calculation.
  const [jobValueText, setJobValueText] = useState<string>('');

  // Default the trade select to the first option once the catalog loads.
  useEffect(() => {
    if (!tradeKey && trades.length > 0) setTradeKey(trades[0].key);
  }, [tradeKey, trades]);

  // Reset the editable job value to the selected trade's niche default whenever the trade changes (including the initial default selection).
  useEffect(() => {
    const current = trades.find((option) => option.key === tradeKey) ?? trades[0];
    if (current) setJobValueText(String(current.avgJobValue));
  }, [tradeKey, trades]);

  const trade = trades.find((option) => option.key === tradeKey) ?? trades[0];
  const nicheJobValue = trade ? trade.avgJobValue : 0;
  // Use the entered value only when it is a valid positive number within the allowed range; any non-number, value <= roiJobValueMin, or value > roiJobValueMax falls back to the trade's niche default.
  const parsedJobValue = Number(jobValueText);
  const jobValueValid = Number.isFinite(parsedJobValue) && parsedJobValue > LandingConstants.roiJobValueMin && parsedJobValue <= LandingConstants.roiJobValueMax;
  const avgJobValue = jobValueValid ? parsedJobValue : nicheJobValue;
  const monthlyCalls = calls * LandingConstants.roiWeeksPerMonth;
  const jobsLost = monthlyCalls * (closeRate / LandingConstants.percentDivisor);
  const monthlyLoss = jobsLost * avgJobValue;
  const annualLoss = monthlyLoss * LandingConstants.monthsPerYear;
  const netRecovery = monthlyLoss - props.echoMonthlyCost;

  // Every monetary value in the card renders with the same currency symbol the pricing cards resolved to (USD fallback when a plan has no override for the visitor's currency).
  const currencySymbol = GetCurrencySymbol(props.currency);
  const netDisplay = netRecovery > 0 ? `${FormatCurrency(netRecovery, currencySymbol)}${Localized.SharedPerMonth}` : Localized.LandingRoiNetEven;

  // On blur, snap an invalid entry back to the niche default so the field never displays a value the calculation is not using.
  const handleJobValueBlur = () => {
    if (!jobValueValid) setJobValueText(String(nicheJobValue));
  };

  // Clamp entries above the max down to it as they are typed (keeps the digit count - and the card layout - bounded); empty and other values pass through to the validity/blur handling.
  const handleJobValueChange = (raw: string) => {
    const num = Number(raw);
    if (raw !== '' && Number.isFinite(num) && num > LandingConstants.roiJobValueMax) {
      setJobValueText(String(LandingConstants.roiJobValueMax));
      return;
    }
    setJobValueText(raw);
  };

  return (
    <section className={`${Styles.cardInner} ${Styles.cardRoi}`}>
      <div className={Styles.cardTag}>
        <Label text={Localized.LandingRoiLabel} classNameContainer={Styles.sectionLabel} />
        {/* Structural wrapper: a single Label cannot render the two-line styled headline, so a div is used and each line carries the tooltip. */}
        <div className={Styles.sectionTitle}>
          <Label text={Localized.LandingRoiTitle1} title={`${Localized.LandingRoiTitle1} ${Localized.LandingRoiTitle2}`} />
          <br />
          <Label text={Localized.LandingRoiTitle2} title={`${Localized.LandingRoiTitle1} ${Localized.LandingRoiTitle2}`} classNameContainer={Styles.accent2} />
        </div>
      </div>
      <div className={Styles.cardBody}>
        <div className={Styles.roiWrap}>
          <div className={Styles.roiInputs}>
            <div className={Styles.inputGroup}>
              <Label text={Localized.LandingRoiTradeLabel} classNameContainer={Styles.inputLabel} />
              <div className={Styles.selectWrap}>
                <select className={Styles.inputSelect} value={tradeKey} onChange={(event) => setTradeKey(event.target.value)}>
                  {trades.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={Styles.inputGroup}>
              <Label text={Localized.LandingRoiJobValueLabel} classNameContainer={Styles.inputLabel} />
              <div className={Styles.currencyInputWrap}>
                <Label text={currencySymbol} classNameContainer={Styles.currencyPrefix} />
                {/* Raw <input>: number input needing min/max attributes, which engine Textbox does not forward. */}
                <input
                  className={Styles.inputText}
                  type="number"
                  min={LandingConstants.roiJobValueMin}
                  max={LandingConstants.roiJobValueMax}
                  value={jobValueText}
                  onChange={(event) => handleJobValueChange(event.target.value)}
                  onBlur={handleJobValueBlur}
                />
              </div>
            </div>
            <RangeSlider
              label={Localized.LandingRoiCallsLabel}
              unit={Localized.LandingRoiCallsUnit}
              min={LandingConstants.roiCallsMin}
              max={LandingConstants.roiCallsMax}
              value={calls}
              minLabel={String(LandingConstants.roiCallsMin)}
              midLabel={String(Math.round((LandingConstants.roiCallsMin + LandingConstants.roiCallsMax) / LandingConstants.midpointDivisor))}
              maxLabel={String(LandingConstants.roiCallsMax)}
              onChange={setCalls}
            />
            <RangeSlider
              label={Localized.LandingRoiCloseLabel}
              unit={Localized.LandingRoiCloseUnit}
              min={LandingConstants.roiCloseMin}
              max={LandingConstants.roiCloseMax}
              value={closeRate}
              minLabel={`${LandingConstants.roiCloseMin}%`}
              midLabel={`${Math.round((LandingConstants.roiCloseMin + LandingConstants.roiCloseMax) / LandingConstants.midpointDivisor)}%`}
              maxLabel={`${LandingConstants.roiCloseMax}%`}
              onChange={setCloseRate}
            />
          </div>
          <div className={Styles.roiOutput}>
            <Label text={Localized.LandingRoiOutputTop} classNameContainer={Styles.roiLabelTop} />
            <div className={Styles.roiNumberRow}>
              <Label text={Localized.LandingRoiAnnualLossLabel} classNameContainer={Styles.roiNumberLabel} />
              <Label text={FormatCurrency(annualLoss, currencySymbol)} classNameContainer={Styles.roiNumber} />
            </div>
            <div className={Styles.roiDivider} />
            <div className={Styles.roiBreakdown}>
              <RoiRow label={Localized.LandingRoiRowJobValue} value={`${FormatCurrency(avgJobValue, currencySymbol)} ${Localized.LandingRoiAvgSuffix}`} />
              <RoiRow label={Localized.LandingRoiRowMonthlyCalls} value={`${Math.round(monthlyCalls)} ${Localized.LandingRoiCallsUnitShort}`} />
              <RoiRow label={Localized.LandingRoiRowJobsLost} value={`${Math.round(jobsLost)} ${Localized.LandingRoiJobsUnit}`} />
              <RoiRow label={Localized.LandingRoiRowMonthlyLoss} value={FormatCurrency(monthlyLoss, currencySymbol)} tone="neg" />
              <RoiRow label={FormatLocalized(Localized.LandingRoiRowEchoCost, props.echoPlanName)} value={`${FormatCurrency(props.echoMonthlyCost, currencySymbol)}${Localized.SharedPerMonth}`} />
              <RoiRow label={Localized.LandingRoiRowNetRecovery} value={netDisplay} tone="pos" />
            </div>
            <Button text={Localized.LandingRoiCTA} classNameButtonEnabled={Styles.roiCta} onClickHandler={props.onRecover} />
            <Label text={Localized.LandingRoiNote} classNameContainer={Styles.roiNote} />
          </div>
        </div>
      </div>
    </section>
  );
}
